import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Divider, Grid, Stack, TextField, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useBookingByIdWithMetaQueryObserver } from "../../../../../../../api/services";
import { setDecimalSeparator } from "../../../../../../../utils";
import { RouteParams } from "../../../../../../../types";

const PaymentDetails = () => {

    const { t, i18n } = useTranslation('booking');

    const { id } = useParams<RouteParams>();

    const bookingQuery = useBookingByIdWithMetaQueryObserver(id);

    return (
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h5">
                    {t('label.paymentDetails')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container rowSpacing={1} columnSpacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('label.secondCreditCardRequired')}
                            value={bookingQuery.data?.booking.secondCreditCardRequired ? t('label.yes') : t('label.no')}
                            fullWidth
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('label.paymentProvider')}
                            value={bookingQuery.data?.booking.paymentProvider ?? ''}
                            fullWidth
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('label.creditType')}
                            value={bookingQuery.data?.booking.creditType ?? ''}
                            fullWidth
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('label.paymentStatus')}
                            value={bookingQuery.data?.booking.paymentStatus ?? ''}
                            fullWidth
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('label.paymentCode')}
                            value={bookingQuery.data?.booking.paymentCode ?? ''}
                            fullWidth
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('label.paymentCodeDescription')}
                            value={!!bookingQuery.data?.booking.paymentCodeDescription ? t(`payment_methods.${bookingQuery.data?.booking.paymentCodeDescription}`) : ''}
                            fullWidth
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Grid container direction="row" lg={12} rowSpacing={0} width="100%">
                    <Grid item xs={0} sm={6} md={0} lg={6} >
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <Stack direction="row" justifyContent="space-between" width="100%">
                            <Typography
                                variant="h6"
                            >
                                {t('label.summary')}
                            </Typography>
                            <Typography
                                variant="h6"
                                textAlign="end"
                            >
                                {t('label.total')}
                            </Typography>
                        </Stack>
                        {bookingQuery.data?.booking.totals.map((v, i) => (
                            <Stack
                                key={i}
                                direction="row"
                                justifyContent="space-between" width="100%"
                            >
                                <Grid container>
                                    <Grid item xs={6}>
                                <Typography
                                    boxSizing="border-box"
                                    pl={v.subrow ? 2 : 0}
                                    variant={v.codeTypeTotal === "TOT" ? 'h6' : 'inherit'}
                                >
                                    {t(`label.excesses.${v.codeTypeTotal}`, { defaultValue: v.description })}
                                </Typography>
                                    </Grid>
                                    <Grid item xs={3} textAlign="end">
                                        {!!v.originalAmount && v.originalAmount !== v.amount &&
                                            <Typography
                                                textAlign="end"
                                                variant="caption"
                                            >
                                                {'('}
                                                <span
                                                    style={{
                                                        textDecoration: 'line-through'
                                                    }}
                                                >
                                                    {`${setDecimalSeparator((v.originalAmount / 100).toFixed(2), i18n.language)} ${v.currency}`}
                                                </span>
                                                {') '}
                                            </Typography>
                                        }
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography
                                            textAlign="end"
                                            variant={v.codeTypeTotal === "TOT" ? 'h6' : 'inherit'}
                                        >
                                            {`${setDecimalSeparator((v.amount / 100).toFixed(2), i18n.language)} ${v.currency}`}
                                        </Typography>
                                    </Grid>
                                    </Grid>
                            </Stack>
                        ))}

                        <Box sx={{paddingTop: 1}}>
                            {bookingQuery?.data?.booking?.salesChannel === 'JIMPI' &&
                                <>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between" width="100%"
                                    >
                                        <Typography
                                            boxSizing="border-box"
                                            pl={0}
                                            variant={'h6'}
                                        >
                                            {t('label.firstInvoiceTotal')}
                                        </Typography>
                                        <Typography
                                            textAlign="end"
                                            variant={'h6'}
                                        >
                                            {setDecimalSeparator((bookingQuery?.data?.booking?.voucherInvoiceAmount / 100 ?? 0).toFixed(2), i18n.language)}
                                            {!!bookingQuery?.data?.booking?.totals[0]?.currency ?  ' ' + bookingQuery.data?.booking?.totals[0]?.currency : ' EUR'}
                                        </Typography>
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between" width="100%"
                                    >
                                        <Typography
                                            boxSizing="border-box"
                                            pl={0}
                                            variant={'h6'}
                                        >
                                            {t('label.secondInvoiceTotal')}
                                        </Typography>
                                        <Typography
                                            textAlign="end"
                                            variant={'h6'}
                                        >
                                            {setDecimalSeparator((bookingQuery?.data?.booking?.driverInvoiceAmount / 100 ?? 0).toFixed(2), i18n.language)}
                                            {!!bookingQuery?.data?.booking?.totals[0]?.currency ? ' ' + bookingQuery.data?.booking?.totals[0]?.currency : ' EUR'}
                                        </Typography>
                                    </Stack>
                                </>
                            }
                        </Box>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
};

export default PaymentDetails;
